import { useEffect, useReducer, useRef, useCallback } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { UseTabManagementProps, DraggedItem, DropPointer } from "./types";
import { tabsReducer, initialState } from "./reducer";
import { validateTabName, trim, tempId } from "./utils";

export const useTabsState = (props: UseTabManagementProps) => {
  const [state, dispatch] = useReducer(tabsReducer, props.tabs, initialState);
  const newTabInputRef = useRef<HTMLInputElement>(null);
  const propsTabsRef = useRef(props.tabs);

  // sync from props (e.g., when parent adds a server-created tab)
  useEffect(() => {
    // Only sync if props reference actually changed
    if (props.tabs !== propsTabsRef.current) {
      propsTabsRef.current = props.tabs;
      dispatch({ type: "SYNC_FROM_PROPS", tabs: props.tabs });
    }
  }, [props.tabs]);

  const setLocalTabs = useCallback(
    (tabs: TabData[]) => dispatch({ type: "SET_LOCAL_TABS", tabs }),
    [],
  );

  // add flow
  const startAdd = useCallback(() => {
    if (state.localTabs.length < 20) dispatch({ type: "START_ADD" });
  }, [state.localTabs.length]);

  const cancelAdd = useCallback(() => {
    dispatch({ type: "CANCEL_ADD" });
  }, []);

  const setNewTabName = useCallback((value: string) => {
    dispatch({ type: "SET_NEW_TAB_NAME", value });
  }, []);

  const addOptimistic = useCallback((label: string) => {
    const err = validateTabName(label);
    if (err) {
      dispatch({ type: "SET_NEW_TAB_ERROR", value: err });
      return null;
    }
    const tab: TabData = { id: tempId(), label: trim(label), groupName: null };
    dispatch({ type: "ADD_OPTIMISTIC_TAB", tab });
    return tab;
  }, []);

  // rename + blur validation
  const updateLabel = useCallback((id: string, label: string) => {
    dispatch({ type: "UPDATE_TAB_NAME", id, label });
  }, []);

  const validateOnBlur = useCallback(
    (id: string, label: string, original?: string) => {
      const err = validateTabName(label);
      if (err) {
        dispatch({ type: "SET_TAB_ERROR", id, message: err });
        if (typeof original === "string")
          dispatch({ type: "REVERT_TAB_NAME", id, label: original });
      } else {
        dispatch({ type: "CLEAR_TAB_ERROR", id });
      }
    },
    [],
  );

  // hover
  const hoverTab = useCallback(
    (tabId: string | null) => dispatch({ type: "HOVER_TAB", tabId }),
    [],
  );
  const hoverNewItem = useCallback(
    (value: boolean) => dispatch({ type: "HOVER_NEW_ITEM", value }),
    [],
  );

  // dragging
  // dragOverTarget is only ever set on dragenter and cleared on dragend/drop —
  // deliberately not cleared on dragleave. Native HTML5 dragenter/dragleave
  // ordering between adjacent/nested elements isn't guaranteed (it can differ
  // by drag direction and DOM nesting), so a leave-driven "clear if counter
  // hits zero" approach can desync and silently drop the hover target. Always
  // trusting the most recent dragenter is simpler and can't get out of sync.
  const dragStart = useCallback(
    (item: DraggedItem) => dispatch({ type: "DRAG_START", dragged: item }),
    [],
  );
  const dragEnd = useCallback(() => {
    dispatch({ type: "DRAG_END" });
  }, []);
  const dragOver = useCallback(
    (target: DropPointer | null) => dispatch({ type: "DRAG_OVER", target }),
    [],
  );

  // deletion
  const openDeleteConfirm = useCallback(
    (tabId: string, tabName: string, hasContent = false, affectedGroupName: string | null = null) => {
      dispatch({
        type: "OPEN_DELETE_CONFIRM",
        payload: { tabId, tabName, hasContent, affectedGroupName },
      });
    },
    [],
  );
  const closeDeleteConfirm = useCallback(
    () => dispatch({ type: "CLOSE_DELETE_CONFIRM" }),
    [],
  );
  const setDeleteHasContent = useCallback(
    (hasContent: boolean) =>
      dispatch({ type: "SET_DELETE_HAS_CONTENT", hasContent }),
    [],
  );
  const openGroupDeleteConfirm = useCallback(
    (groupName: string) => dispatch({ type: "OPEN_GROUP_DELETE_CONFIRM", groupName }),
    [],
  );
  const closeGroupDeleteConfirm = useCallback(
    () => dispatch({ type: "CLOSE_GROUP_DELETE_CONFIRM" }),
    [],
  );

  return {
    state,
    refs: { newTabInputRef },
    actions: {
      setLocalTabs,
      startAdd,
      cancelAdd,
      setNewTabName,
      addOptimistic,
      updateLabel,
      validateOnBlur,
      hoverTab,
      hoverNewItem,
      dragStart,
      dragEnd,
      dragOver,
      openDeleteConfirm,
      closeDeleteConfirm,
      setDeleteHasContent,
      openGroupDeleteConfirm,
      closeGroupDeleteConfirm,
    },
  } as const;
};
